import React from 'react'
import { Observable } from 'rxjs'
import { formatPrice, formatCurrency } from '../lib/currency-formatter'
import {
  INPUT_POSITION_PRICE,
  INPUT_POSITION_STOPLOSS,
  INPUT_POSITION_TAKEPROFIT,
} from '../constants'
import { MiddlewareAPI } from 'redux'
import { Epic } from 'redux-observable'
import { InputPositionPrice, InputPositionSL, InputPositionTP } from '../actions/TradeActions'
import { ModifyTrade, TradeCommandType, TradeType } from '@bdswiss/mt4-connector'
import { RootState, PositionInput } from '../types'
import PLInput from './PLInput'
import { I18n, Translate } from 'react-redux-i18n'
import {
  PriceExtended,
  Trade,
  calcTpInitial,
  calcSlInitial,
  calcPendingPriceValue,
} from '@bdswiss/mt4-connector'
import './PositionEdit.css'

export interface Props {
  accountLoaded: boolean
  position: Trade
  positionInput: PositionInput
  selectedPosition?: number
  currency: string
  locale: string
  isPending: boolean
  asset?: PriceExtended
  tradeOperationEnabled: boolean
  favourites: string[]
  onSave: (trade: ModifyTrade, tradeType: TradeType) => void
  onClose: () => void
  onInputStopLoss: (order: number, value: number) => void
  onInputTakeProfit: (order: number, value: number) => void
  onInputPrice: (order: number, value: number) => void
  onShowChart: (symbol: string, tradeType: TradeCommandType) => void
  onToggleMenu: () => void
  onToggleFavourite: (favourites: string[]) => void
}

export interface State {
  takeProfit: number
  stopLoss: number
  price: number
  type: TradeType
  modified: boolean
  favourites: { [symbol: string]: { confirmed: boolean; operation: 'on' | 'off' } } // true if confirmed from server
  inputValid: boolean
}

let editComponent: PositionEdit

export const processPositionEditInput: Epic<
  InputPositionSL | InputPositionTP | InputPositionPrice,
  RootState
> = (action$, state: MiddlewareAPI<RootState>) =>
  action$
    .ofType(INPUT_POSITION_STOPLOSS, INPUT_POSITION_TAKEPROFIT, INPUT_POSITION_PRICE)
    .mergeMap(action => {
      if (action.order > 0) {
        switch (action.type) {
          case INPUT_POSITION_STOPLOSS:
            editComponent.setState(prevState => ({
              stopLoss: action.stopLoss,
            }))
            break
          case INPUT_POSITION_TAKEPROFIT:
            editComponent.setState(prevState => ({
              takeProfit: action.takeProfit,
            }))
            break
          case INPUT_POSITION_PRICE:
            editComponent.setState(prevState => ({
              price: action.price,
            }))
            break
        }
      }
      return Observable.empty()
    })

class PositionEdit extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    editComponent = this

    this.setTakeProfit = this.setTakeProfit.bind(this)
    this.setStopLoss = this.setStopLoss.bind(this)
    this.setPrice = this.setPrice.bind(this)
    this.toggleFavourite = this.toggleFavourite.bind(this)

    this.state = {
      takeProfit: this.props.positionInput.tpInput || this.props.position.tp,
      stopLoss: this.props.positionInput.slInput || this.props.position.sl,
      price: this.props.positionInput.priceInput || this.props.position.openPrice,
      type: this.props.position.type,
      modified: false,
      favourites: this.props.favourites.reduce(
        (acc, cur) => {
          acc[cur] = { confirmed: true, operation: 'on' }
          return acc
        },
        {} as any
      ),
      inputValid: true,
    }
  }

  public render() {
    const {
      position,
      positionInput,
      currency,
      locale,
      asset,
      isPending,
      onClose,
      onSave,
      tradeOperationEnabled,
      // selectedPosition,
      // onShowChart,
      accountLoaded,
      onToggleMenu,
    } = this.props
    const { modified, inputValid } = this.state
    const precision = asset ? asset.digits : 5
    const tradeType = /buy/i.test(position.type) ? 'buy' : 'sell'
    const favourite = Object.keys(this.state.favourites).find(key => key === position.symbol)
    const confirmed = favourite ? this.state.favourites[favourite].confirmed : true
    return (
      <div className="edit-wrapper" key={position.order}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="sidebar__title" data-cy="tradeSymbol">
            <i
              onClick={() => this.toggleFavourite(position.symbol)}
              className={`favourite favourite-trade ${
                favourite ? 'icon-star-full' : 'icon-star-empty'
              } ${!confirmed ? 'unconfirmed' : ''}`}
              title="Favourite"
            />
            {asset ? (asset as any).label : position.symbol}
          </h2>
          <button onClick={onToggleMenu} className="btn close_btn" data-cy="closeTrade">
            <i className="icon-close-new" />
          </button>
        </div>

        <div>
          <table className="edit-wrapper__table">
            <tbody>
              <tr>
                <td>
                  <Translate value={'orderId'} />
                </td>
                <td>{position.order}</td>
              </tr>
              <tr>
                <td>
                  <Translate value={'direction'} />
                </td>
                <td>
                  <div
                    className={
                      /buy/i.test(position.type)
                        ? 'bdswiss-table--positive'
                        : 'bdswiss-table--negative'
                    }
                  >
                    <i className={/buy/i.test(position.type) ? 'icon-buy-new' : 'icon-sell-new'} />
                    {I18n.t(position.type)}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <Translate value={'investment'} />
                </td>
                <td>
                  {position.investment
                    ? formatCurrency(position.investment, currency, locale)
                    : `${position.volume} ${I18n.t('lots', {
                        count: position.volume === 1 ? 1 : 0,
                      })}`}
                </td>
              </tr>
              <tr>
                <td>
                  <Translate value={'leverage'} />
                </td>
                <td>{`1:${position.leverage}`}</td>
              </tr>
              {asset ? (
                <tr>
                  <td>
                    <Translate value={'value'} />
                  </td>
                  <td>
                    {formatCurrency(
                      position.volume * asset.lotSize,
                      asset.contractType,
                      this.props.locale
                    )
                      .replace(/0+$/g, '')
                      .replace(/\.$/, '.00')}
                  </td>
                </tr>
              ) : (
                undefined
              )}
              {!isPending ? (
                <tr>
                  <td>
                    <Translate value={'openPrice'} />
                  </td>
                  <td>{formatPrice(position.openPrice, precision, '')}</td>
                </tr>
              ) : (
                undefined
              )}
              {asset ? (
                <tr>
                  <td>
                    <Translate value={'currentPrice'} />
                  </td>
                  <td>
                    {formatPrice(
                      /buy/i.test(position.type) ? asset.mask : asset.mbid,
                      precision,
                      ''
                    )}
                  </td>
                </tr>
              ) : (
                undefined
              )}
              {!isPending && 'swaps' in position ? (
                <>
                  <tr>
                    <td>
                      <Translate value={'swaps'} />
                    </td>
                    <td>{formatCurrency(position.swaps!, currency, locale)}</td>
                  </tr>
                  <tr>
                    <td>
                      <Translate value={'commission'} />
                    </td>
                    <td>{formatCurrency(position.commission!, currency, locale, currency === 'CUD' ? {precision: 2} : {})}</td>
                  </tr>
                </>
              ) : (
                undefined
              )}
            </tbody>
          </table>

          {asset ? (
            <div className="pb-2" data-cy="positionSettings">
              <div className="d-flex flex-row justify-content-between edit-wrapper__title">
                <Translate value={'positionSettings'} />
                {/* <div onClick={onShowChart.bind(null, position.symbol, /buy/i.test(position.type) ? 'buy' : 'sell')} className="btn-chart">
                        <i className="icon-chart" /> {I18n.t("showChart")}
                    </div> */}
              </div>
              {isPending ? (
                <PLInput
                  key={`${tradeType}Price`}
                  type={'price'}
                  hideSwitch={true}
                  enabled={true}
                  tradeType={tradeType}
                  label={I18n.t('openPrice')}
                  asset={asset}
                  valueInitial={calcPendingPriceValue(tradeType, asset, asset.currentRate)}
                  value={this.state.price}
                  currency={currency}
                  locale={locale}
                  setValue={this.setPrice}
                  accountLoaded={accountLoaded}
                  onInputValid={this.setInputValidityStatus}
                  isPending={isPending}
                />
              ) : (
                undefined
              )}
              {/* prettier-ignore */}
              <PLInput
                type={'profit'}
                tradeType={/buy/i.test(position.type) ? 'buy' : 'sell'}
                label={I18n.t('takeProfit')}
                position={position}
                positionInput={positionInput || {}}
                asset={asset}
                valueInitial={
                  this.props.position.tp ||
                  calcTpInitial(
                    position,
                    asset,
                    isPending
                      ? this.state.price
                      : /buy/i.test(position.type)
                        ? Math.max(position.openPrice, asset.mbid)
                        : Math.min(position.openPrice, asset.mask),
                    isPending
                  )
                }
                value={this.state.takeProfit}
                currency={currency}
                locale={locale}
                setValue={this.setTakeProfit}
                accountLoaded={accountLoaded}
                onInputValid={this.setInputValidityStatus}
                isPending={isPending}
              />
              {/* prettier-ignore */}
              <PLInput
                type={'loss'}
                tradeType={/buy/i.test(position.type) ? 'buy' : 'sell'}
                label={I18n.t('stopLoss')}
                position={position}
                positionInput={positionInput || {}}
                asset={asset}
                valueInitial={
                  this.props.position.sl ||
                  calcSlInitial(
                    position,
                    asset,
                    isPending
                      ? this.state.price
                      : undefined,
                    isPending
                  )
                }
                value={this.state.stopLoss}
                currency={currency}
                locale={locale}
                setValue={this.setStopLoss}
                accountLoaded={accountLoaded}
                onInputValid={this.setInputValidityStatus}
                isPending={isPending}
              />
            </div>
          ) : (
            undefined
          )}

          <div className="d-flex justify-content-between mt-3">
            <div className="d-flex flex-fill pr-2">
              <button
                onClick={onClose}
                className="btn btn-danger btn-block"
                disabled={!tradeOperationEnabled}
                data-cy="closePosition"
              >
                {isPending ? <Translate value={'cancel'} /> : <Translate value={'close'} />}
              </button>
            </div>
            <div className="d-flex flex-fill pl-2 ">
              {asset ? (
                <button
                  onClick={() =>
                    onSave(
                      isPending
                        ? {
                            order: position.order,
                            uuid: position.uuid!,
                            tp: this.state.takeProfit,
                            sl: this.state.stopLoss,
                            price: this.state.price,
                          }
                        : {
                            order: position.order,
                            uuid: position.uuid!,
                            tp: this.state.takeProfit,
                            sl: this.state.stopLoss,
                          },
                      this.state.type
                    )
                  }
                  className="btn btn-primary btn-block"
                  disabled={!canSave(modified, tradeOperationEnabled) || !inputValid}
                  data-cy="savePosition"
                >
                  <Translate value={'save'} />
                </button>
              ) : (
                undefined
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    const { sl, tp, openPrice } = this.props.position
    const { sl: oldSl, tp: oldTp, openPrice: oldOpenPrice } = prevProps.position
    const {
      stopLoss: stateSl,
      takeProfit: stateTp,
      price: statePrice,
      modified: oldModified,
    } = this.state
    const newState: any = {}
    if (oldSl !== sl) {
      newState.stopLoss = sl
    }
    if (oldTp !== tp) {
      newState.takeProfit = tp
    }
    if (oldOpenPrice !== openPrice) {
      newState.price = openPrice
    }
    const modified = oldTp !== stateTp || oldSl !== stateSl || oldOpenPrice !== statePrice
    if (modified !== oldModified) {
      newState.modified = modified
    }
    if (Object.keys(newState).length) {
      this.setState(pState => newState)
    }
    if (prevProps.favourites !== this.props.favourites) {
      this.setState(pState => ({
        favourites: this.props.favourites.reduce(
          (acc, cur) => {
            acc[cur] = { confirmed: true, operation: 'on' }
            return acc
          },
          {} as any
        ),
      }))
    }
  }

  private setTakeProfit(value: number) {
    // console.log('profit '+value);
    this.props.onInputTakeProfit(this.props.position.order, value)
  }

  private setStopLoss(value: number) {
    // console.log('loss '+value);
    this.props.onInputStopLoss(this.props.position.order, value)
  }

  private setPrice(value: number) {
    // console.log('price '+value);
    this.props.onInputPrice(this.props.position.order, value)
  }

  private toggleFavourite(favourite: string) {
    const { favourites } = this.state
    if (Object.keys(favourites).includes(favourite)) {
      favourites[favourite] = { confirmed: false, operation: 'off' }
    } else {
      favourites[favourite] = { confirmed: false, operation: 'on' }
    }
    this.props.onToggleFavourite(
      Object.keys(favourites).filter(key => favourites[key].operation === 'on')
    )
    this.setState(prevState => ({
      favourites,
    }))
  }

  private setInputValidityStatus = (inputValid: boolean) => {
    this.setState({ inputValid })
  }
}

function canSave(modified: boolean, tradeOperationEnabled: boolean): boolean {
  return modified && tradeOperationEnabled
}

export default PositionEdit
